(() => {
  if (customElements.get('sb-premium-home')) return;
  class SbPremiumHome extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.controls = [...this.querySelectorAll('[data-sb-game-select]')];
      this.slides = [...this.querySelectorAll('[data-sb-slide]')];
      this.reducedMotion = matchMedia('(prefers-reduced-motion: reduce)');
      this.finePointer = matchMedia('(pointer: fine)');
      this.listeners = new AbortController();
      this.carousel = this.querySelector('[data-sb-carousel]');
      this.currentIndex = Math.max(0, this.slides.findIndex(slide => !slide.hidden));
      this.transition = 0;
      this.paused = this.reducedMotion.matches || this.slides.length < 2;
      const options = { signal: this.listeners.signal };
      this.setupTabs(options);
      this.setupCarousel(options);
      this.setupScrollEffect(options);
      this.querySelectorAll('[data-sb-home-motion]').forEach(scene => {
        scene.addEventListener('pointermove', event => this.moveScene(scene, event), options);
        scene.addEventListener('pointerleave', () => this.resetScene(scene), options);
      });
      this.addEventListener('shopify:block:select', event => {
        const control = this.controls.find(item => item.dataset.sbGameSelect === event.detail.blockId);
        if (control) this.selectGame(control.dataset.game, false);
      }, options);
      this.showSlide(this.currentIndex, false);
    }
    disconnectedCallback() { this.listeners?.abort(); this.stopTimer(); this.observer?.disconnect(); if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame); this.initialized = false; }
    setupTabs(options) {
      const picker = this.querySelector('.sb-home__game-list');
      if (!picker || !this.controls.length) return;
      picker.setAttribute('role', 'tablist');
      picker.setAttribute('aria-label', this.querySelector('[data-sb-game-picker]').getAttribute('aria-label'));
      this.controls.forEach((control, index) => {
        control.setAttribute('role', 'tab');
        if (this.carousel?.id) control.setAttribute('aria-controls', this.carousel.id);
        control.addEventListener('click', event => {
          if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || event.button !== 0) return;
          event.preventDefault(); this.selectGame(control.dataset.game);
        }, options);
        control.addEventListener('keydown', event => {
          let next;
          if (event.key === 'ArrowRight') next = (index + 1) % this.controls.length;
          if (event.key === 'ArrowLeft') next = (index - 1 + this.controls.length) % this.controls.length;
          if (event.key === 'Home') next = 0;
          if (event.key === 'End') next = this.controls.length - 1;
          if (event.key === ' ' || event.key === 'Enter') { event.preventDefault(); this.selectGame(control.dataset.game); }
          if (next !== undefined) { event.preventDefault(); this.selectGame(this.controls[next].dataset.game); this.controls[next].focus(); }
        }, options);
      });
    }
    selectGame(game, animate = true) { this.paused = this.reducedMotion.matches; this.showFirstGameSlide(game, animate); this.updateTimer(); this.updateToggle(); }
    setupCarousel(options) {
      if (!this.slides.length) return;
      this.toggleButton = this.querySelector('[data-sb-carousel-toggle]');
      this.toggleButton?.addEventListener('click', () => { this.paused = !this.paused; this.updateTimer(); this.updateToggle(); }, options);
      this.carousel?.addEventListener('mouseenter', () => { this.hovered = true; this.updateTimer(); }, options);
      this.carousel?.addEventListener('mouseleave', () => { this.hovered = false; this.updateTimer(); }, options);
      this.carousel?.addEventListener('focusin', () => { this.focused = true; this.updateTimer(); }, options);
      this.carousel?.addEventListener('focusout', () => { this.focused = false; this.updateTimer(); }, options);
      let startX = 0;
      this.carousel?.addEventListener('pointerdown', event => { startX = event.clientX; }, options);
      this.carousel?.addEventListener('pointerup', event => { const distance = event.clientX - startX; if (Math.abs(distance) >= 48) this.manualStep(distance < 0 ? 1 : -1); }, options);
      document.addEventListener('visibilitychange', () => this.updateTimer(), options);
      this.inView = false;
      if ('IntersectionObserver' in window && this.carousel) {
        this.observer = new IntersectionObserver(entries => { this.inView = entries[0].isIntersecting; this.updateTimer(); }, { threshold: .25 });
        this.observer.observe(this.carousel);
      } else this.inView = true;
      this.updateToggle(); this.updateTimer();
    }
    manualStep(direction) { this.paused = this.reducedMotion.matches; this.showSlide(this.currentIndex + direction); this.updateTimer(); this.updateToggle(); }
    showFirstGameSlide(game, animate = true) { const index = this.slides.findIndex(slide => slide.dataset.game === game); if (index >= 0) this.showSlide(index, animate); }
    showSlide(index, animate = true) {
      if (!this.slides.length) return;
      this.activeAnimations?.forEach(animation => animation.cancel());
      this.activeAnimations = [];
      this.currentIndex = (index + this.slides.length) % this.slides.length;
      const current = this.slides[this.currentIndex];
      const previous = this.visibleSlide || this.slides.find(slide => !slide.hidden);
      const changed = previous && previous !== current;
      this.slides.forEach(slide => { if (slide !== current && slide !== previous) slide.hidden = true; });
      this.visibleSlide = current;
      if (changed && animate && !this.reducedMotion.matches) current.hidden = false;
      else this.slides.forEach((slide, slideIndex) => { slide.hidden = slideIndex !== this.currentIndex; });
      this.slides.forEach(slide => {
        const selected = slide === current;
        slide.inert = !selected;
        slide.setAttribute('aria-hidden', String(!selected));
      });
      if (changed && previous.contains(document.activeElement)) current.querySelector('a, button, [tabindex]:not([tabindex="-1"])')?.focus({ preventScroll: true });
      this.dataset.activeGame = current.dataset.game;
      this.controls.forEach(control => {
        const selected = control.dataset.game === current.dataset.game;
        control.classList.toggle('is-active', selected); control.setAttribute('aria-selected', String(selected)); control.tabIndex = selected ? 0 : -1;
      });
      if (changed && animate && !this.reducedMotion.matches && typeof current.animate === 'function') {
        const transition = ++this.transition;
        const incoming = current.animate([{ opacity: 0, transform: 'translate3d(0,18px,0) scale(.985)' }, { opacity: 1, transform: 'translate3d(0,0,0) scale(1)' }], { duration: 820, easing: 'cubic-bezier(.22,.8,.2,1)', fill: 'both' });
        const outgoing = previous.animate([{ opacity: 1, transform: 'translate3d(0,0,0) scale(1)' }, { opacity: 0, transform: 'translate3d(0,-12px,0) scale(1.015)' }], { duration: 760, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'both' });
        this.activeAnimations = [incoming, outgoing];
        incoming.finished.then(() => {
          if (transition !== this.transition) return;
          previous.hidden = true;
          this.activeAnimations.forEach(animation => animation.cancel());
          this.activeAnimations = [];
        }).catch(() => {});
      }
      this.querySelectorAll('[data-sb-home-motion]').forEach(scene => this.resetScene(scene));
    }
    updateTimer() {
      this.stopTimer();
      if (this.paused || this.hovered || this.focused || document.hidden || this.inView === false || this.reducedMotion.matches || this.slides.length < 2) return;
      this.timer = window.setTimeout(() => { this.showSlide(this.currentIndex + 1); this.updateTimer(); }, 6000);
    }
    stopTimer() { if (this.timer) window.clearTimeout(this.timer); this.timer = null; }
    updateToggle() {
      if (!this.toggleButton) return;
      const playing = !this.paused && !this.reducedMotion.matches;
      this.toggleButton.textContent = playing ? 'Ⅱ' : '▶';
      this.toggleButton.setAttribute('aria-label', playing ? this.toggleButton.dataset.pauseLabel : this.toggleButton.dataset.playLabel);
    }
    setupScrollEffect(options) {
      const hero = this.querySelector('.sb-home__hero');
      const chapter = this.querySelector('.sb-home__worlds');
      const tiles = [...this.querySelectorAll('.sb-home__world')];
      if (!hero && !chapter) return;
      const clamp = value => Math.max(0, Math.min(1, value));
      const update = () => {
        this.scrollFrame = 0;
        const reduced = this.reducedMotion.matches;
        const height = Math.max(1, window.innerHeight);
        // Read all geometry before applying presentation-only variables.
        const heroRect = hero?.getBoundingClientRect();
        const chapterRect = chapter?.getBoundingClientRect();
        const tileRects = reduced ? [] : tiles.map(tile => tile.getBoundingClientRect());
        const progress = heroRect && !reduced ? clamp(-heroRect.top / Math.max(1, heroRect.height * .72)) : 0;
        hero?.style.setProperty('--sb-scroll-progress', progress.toFixed(3));
        const chapterProgress = chapterRect && !reduced ? clamp((height - chapterRect.top) / (height + chapterRect.height)) : .5;
        chapter?.style.setProperty('--sb-chapter-progress', chapterProgress.toFixed(3));
        tiles.forEach((tile, index) => {
          const rect = tileRects[index];
          const value = rect ? clamp((height - rect.top) / (height + rect.height)) : .5;
          tile.style.setProperty('--sb-tile-progress', value.toFixed(3));
        });
      };
      const request = () => { if (!this.scrollFrame) this.scrollFrame = requestAnimationFrame(update); };
      window.addEventListener('scroll', request, { passive: true, signal: options.signal });
      window.addEventListener('resize', request, { passive: true, signal: options.signal });
      this.reducedMotion.addEventListener('change', () => {
        if (this.reducedMotion.matches) {
          this.activeAnimations?.forEach(animation => animation.cancel());
          this.slides.forEach((slide, index) => { slide.hidden = index !== this.currentIndex; });
          this.paused = true;
          this.stopTimer();
          this.updateToggle();
        }
        request();
      }, options);
      request();
    }
    moveScene(scene, event) {
      if (this.reducedMotion.matches || !this.finePointer.matches) return;
      const rect = scene.getBoundingClientRect();
      scene.style.setProperty('--sb-pointer-x', `${((event.clientX - rect.left) / rect.width - .5) * 12}px`);
      scene.style.setProperty('--sb-pointer-y', `${((event.clientY - rect.top) / rect.height - .5) * 10}px`);
    }
    resetScene(scene) { scene.style.setProperty('--sb-pointer-x', '0px'); scene.style.setProperty('--sb-pointer-y', '0px'); }
  }
  customElements.define('sb-premium-home', SbPremiumHome);
})();

